import { useEffect, useState } from "react";
import { getPayments, getRooms, confirmPayment, rejectPayment, sendBulkReminder, declarePayment, deletePayment } from "../../firebase/services";
import { Badge, EmptyState, Spinner, Modal, Btn, Input } from "../../components/UI";
import { genererQuittance } from "../../utils/quittance";

const LOYER = 27500;
const METHODS = ["Orange Money", "Wave", "Espèces", "Virement"];
const METHOD_ICONS = { "Orange Money":"🟠", "Wave":"🌊", "Espèces":"💵", "Virement":"🏦" };
const MOIS = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];

function monthLabel(ts) {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString("fr-FR", { month: "long", year: "numeric" });
}

function formatMonthValue(val) {
  if (!val) return "";
  try {
    const [year, month] = val.split("-");
    return `${MOIS[parseInt(month) - 1]} ${year}`;
  } catch(e) { return val; }
}

// Clé de tri pour un mois "2026-06" → utilisée pour trier et grouper
function getMonthKey(p) {
  if (p.month) return p.month; // "2026-06"
  if (p.createdAt) {
    const d = p.createdAt.toDate ? p.createdAt.toDate() : new Date(p.createdAt);
    return d.toISOString().slice(0, 7);
  }
  return "0000-00";
}

function getMonthOptions() {
  const now = new Date();
  const options = [];
  for (let i = -11; i <= 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const value = d.toISOString().slice(0, 7);
    const label = i === 0
      ? `${MOIS[d.getMonth()]} ${d.getFullYear()} (en cours)`
      : `${MOIS[d.getMonth()]} ${d.getFullYear()}`;
    options.push({ value, label });
  }
  return options;
}

// Regroupe les paiements confirmés par mois, du plus récent au plus ancien
function groupConfirmedByMonth(payments) {
  const groups = {};
  payments
    .filter(p => p.status === "confirmed")
    .forEach(p => {
      const key = getMonthKey(p);
      if (!groups[key]) groups[key] = [];
      groups[key].push(p);
    });
  // Trier les clés du plus récent au plus ancien
  return Object.entries(groups).sort((a, b) => b[0].localeCompare(a[0]));
}

export default function OwnerPayments() {
  const [payments, setPayments]     = useState([]);
  const [rooms, setRooms]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [filter, setFilter]         = useState("all");
  const [selected, setSelected]     = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [rejecting, setRejecting]   = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [showReject, setShowReject] = useState(false);
  const [reminderSent, setReminderSent] = useState(false);

  // Mois déroulé dans la vue "Confirmés"
  const [openMonth, setOpenMonth] = useState(null);

  // Modal validation directe
  const [showDirectModal, setShowDirectModal] = useState(false);
  const [directForm, setDirectForm] = useState({
    roomId: "",
    method: "Espèces",
    reference: "",
    month: new Date().toISOString().slice(0, 7),
  });
  const [directSaving, setDirectSaving] = useState(false);

  const monthOptions = getMonthOptions();

  useEffect(() => {
    const unsubs = [
      getPayments(data => { setPayments(data); setLoading(false); }),
      getRooms(setRooms),
    ];
    return () => unsubs.forEach(u => u());
  }, []);

  // Ouvrir automatiquement le mois le plus récent au premier chargement
  useEffect(() => {
    if (filter === "confirmed" && payments.length > 0 && openMonth === null) {
      const groups = groupConfirmedByMonth(payments);
      if (groups.length > 0) setOpenMonth(groups[0][0]);
    }
  }, [filter, payments, openMonth]);

  const roomName = (id) => {
    const r = rooms.find(r => r.id === id);
    return r ? `Ch. ${r.number}${r.tenantName ? ` — ${r.tenantName}` : ""}` : "?";
  };

  const confirmed = payments.filter(p => p.status === "confirmed");
  const pending   = payments.filter(p => p.status === "pending");
  const rejected  = payments.filter(p => p.status === "rejected");

  // Paiements orphelins : sans chambre valide (roomId manquant ou chambre supprimée)
  const orphanPayments = payments.filter(p => !p.roomId || !rooms.find(r => r.id === p.roomId));

  const filteredNonConfirmed = filter === "pending"  ? pending
                             : filter === "rejected" ? rejected
                             : filter === "all"      ? payments
                             : [];

  const confirmedGroups = groupConfirmedByMonth(payments);

  const [deletingId, setDeletingId] = useState(null);
  const handleDeletePayment = async (paymentId) => {
    setDeletingId(paymentId);
    await deletePayment(paymentId);
    setDeletingId(null);
  };

  const handleConfirm = async () => {
    setConfirming(true);
    await confirmPayment(selected.id, selected.roomId);
    setConfirming(false);
    setSelected(null);
  };

  const handleReject = async () => {
    setRejecting(true);
    await rejectPayment(selected.id, selected.roomId, rejectReason.trim());
    setRejecting(false);
    setSelected(null);
    setShowReject(false);
    setRejectReason("");
  };

  const handleSendReminders = async () => {
    const lateRooms = rooms.filter(r => r.paymentStatus === "late" || r.paymentStatus === "due_soon");
    await sendBulkReminder(lateRooms);
    setReminderSent(true);
    setTimeout(() => setReminderSent(false), 3000);
  };

  const handleDirectPayment = async () => {
    if (!directForm.roomId) return;
    setDirectSaving(true);
    await declarePayment(directForm.roomId, {
      amount: LOYER,
      method: directForm.method,
      reference: directForm.reference.trim(),
      month: directForm.month,
      monthLabel: formatMonthValue(directForm.month),
      validatedByOwner: true,
    });
    setTimeout(async () => {
      const latest = payments.find(p => p.roomId === directForm.roomId && p.status === "pending");
      if (latest) await confirmPayment(latest.id, directForm.roomId);
      setDirectSaving(false);
      setShowDirectModal(false);
    }, 1500);
  };

  const badgeType  = { confirmed:"ok", pending:"pend", rejected:"late" };
  const badgeLabel = { confirmed:"Confirmé ✓", pending:"À confirmer", rejected:"Rejeté ✗" };

  if (loading) return <div style={S.page}><Spinner /></div>;

  // ── Vue confirmés : groupée par mois ──────────────────────────────────
  const renderConfirmedGroups = () => {
    if (confirmedGroups.length === 0)
      return <EmptyState icon="💳" title="Aucun paiement confirmé" subtitle="Les paiements confirmés apparaîtront ici par mois" />;

    return confirmedGroups.map(([monthKey, group]) => {
      const label = formatMonthValue(monthKey);
      const total = group.length * LOYER;
      const isOpen = openMonth === monthKey;
      return (
        <div key={monthKey} style={S.monthGroup}>
          {/* En-tête cliquable du mois */}
          <button style={S.monthHeader} onClick={() => setOpenMonth(isOpen ? null : monthKey)}>
            <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-start" }}>
              <span style={S.monthTitle}>{label}</span>
              <span style={S.monthSub}>{group.length} paiement{group.length > 1 ? "s" : ""} · {total.toLocaleString("fr-FR")} FCFA</span>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <span style={S.monthBadge}>{group.length} / {rooms.filter(r => r.tenantName).length}</span>
              <span style={{ fontSize:18, color:"#1a3a5c", transition:"transform .2s", transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}>⌄</span>
            </div>
          </button>

          {/* Liste des paiements du mois */}
          {isOpen && (
            <div style={S.monthBody}>
              {group
                .sort((a, b) => (a.roomId || "").localeCompare(b.roomId || ""))
                .map(p => (
                  <div key={p.id} style={S.payCard}>
                    <div style={S.payHeader}>
                      <div style={{ flex:1 }}>
                        <p style={S.payRoom}>{roomName(p.roomId)}</p>
                        <p style={S.payMeta}>
                          {METHOD_ICONS[p.method] || "💳"} {p.method || "—"}
                          {p.reference && <span style={{ fontFamily:"monospace", fontSize:11 }}> · Réf : {p.reference}</span>}
                        </p>
                        {p.validatedByOwner && <p style={{ fontSize:11, color:"#2d6a0a", margin:"2px 0 0" }}>✅ Validé directement par IAM</p>}
                      </div>
                      <div style={{ textAlign:"right" }}>
                        <p style={S.payAmt}>{LOYER.toLocaleString("fr-FR")} FCFA</p>
                        <Badge type="ok">Confirmé ✓</Badge>
                      </div>
                    </div>
                    <button
                      style={S.quittanceBtn}
                      onClick={() => genererQuittance({ payment: p, room: rooms.find(r => r.id === p.roomId) })}
                    >📄 Quittance</button>
                  </div>
                ))
              }
            </div>
          )}
        </div>
      );
    });
  };

  // ── Vue liste plate (Tous / À confirmer / Rejetés) ────────────────────
  const renderFlatList = () => {
    const list = filteredNonConfirmed;
    if (list.length === 0)
      return <EmptyState icon="💳" title="Aucun paiement" subtitle="Les paiements apparaîtront ici" />;

    return list.map(p => (
      <div key={p.id} style={S.payCard} onClick={() => p.status === "pending" && setSelected(p)}>
        <div style={S.payHeader}>
          <div style={{ flex:1 }}>
            <p style={S.payRoom}>{roomName(p.roomId)}</p>
            <p style={{ fontSize:13, fontWeight:700, color:"#1a3a5c", margin:"0 0 2px" }}>
              {p.monthLabel || (p.month ? formatMonthValue(p.month) : null) || monthLabel(p.createdAt) || "Paiement"}
            </p>
            <p style={S.payMeta}>
              {METHOD_ICONS[p.method] || "💳"} {p.method || "—"} · {monthLabel(p.createdAt)}
            </p>
            {p.reference && <p style={S.payRef}>Réf : {p.reference}</p>}
            {p.validatedByOwner && <p style={{ fontSize:11, color:"#2d6a0a", margin:"2px 0 0" }}>✅ Validé directement par IAM</p>}
            {p.rejectReason && <p style={{ fontSize:11, color:"#a32d2d", margin:"2px 0 0" }}>Raison : {p.rejectReason}</p>}
          </div>
          <div style={{ textAlign:"right" }}>
            <p style={S.payAmt}>{LOYER.toLocaleString("fr-FR")} FCFA</p>
            <Badge type={badgeType[p.status]||"pend"}>{badgeLabel[p.status]||"?"}</Badge>
          </div>
        </div>
        {p.status === "pending" && (
          <button style={S.confirmBtn} onClick={e => { e.stopPropagation(); setSelected(p); }}>
            ✓ Confirmer ce paiement
          </button>
        )}
        {p.status === "confirmed" && (
          <button
            style={S.quittanceBtn}
            onClick={e => { e.stopPropagation(); genererQuittance({ payment: p, room: rooms.find(r => r.id === p.roomId) }); }}
          >📄 Quittance</button>
        )}
      </div>
    ));
  };

  return (
    <div style={S.page}>
      <div style={S.topBar}>
        <div>
          <h1 style={S.title}>Paiements</h1>
        </div>
        <button style={S.directBtn} onClick={() => {
          setDirectForm({ roomId: rooms.find(r=>r.tenantName)?.id || "", method:"Espèces", reference:"", month: new Date().toISOString().slice(0,7) });
          setShowDirectModal(true);
        }}>
          ✚ Valider directement
        </button>
      </div>

      {/* Résumé */}
      <div style={S.summaryRow}>
        <div style={S.sumCard}>
          <span style={S.sumNum}>{(confirmed.length * LOYER).toLocaleString("fr-FR")}</span>
          <span style={S.sumLabel}>FCFA reçus</span>
        </div>
        <div style={{ ...S.sumCard, borderColor:"#f5d8a0" }}>
          <span style={{ ...S.sumNum, color:"#8a4c00" }}>{(pending.length * LOYER).toLocaleString("fr-FR")}</span>
          <span style={S.sumLabel}>À confirmer</span>
        </div>
        <div style={{ ...S.sumCard, borderColor:"#f5b8b8" }}>
          <span style={{ ...S.sumNum, color:"#a32d2d", fontSize:13 }}>
            {((rooms.filter(r=>r.tenantName).length - confirmed.length) * LOYER).toLocaleString("fr-FR")}
          </span>
          <span style={S.sumLabel}>Restants</span>
        </div>
      </div>

      {/* Rappels */}
      <div style={{ padding:"0 16px 10px" }}>
        <button style={S.reminderBtn} onClick={handleSendReminders}>
          {reminderSent ? "✓ Rappels envoyés !" : "🔔 Envoyer rappels aux retardataires"}
        </button>
      </div>

      <div style={S.filterBar}>
        {[["all","Tous"],["pending","À confirmer"],["confirmed","Confirmés"],["rejected","Rejetés"]].map(([v,l]) => (
          <button key={v} style={{...S.pill,...(filter===v?S.pillOn:{})}} onClick={() => {
            setFilter(v);
            // Rouvrir le mois le plus récent quand on revient sur "Confirmés"
            if (v === "confirmed") {
              const groups = groupConfirmedByMonth(payments);
              setOpenMonth(groups.length > 0 ? groups[0][0] : null);
            }
          }}>{l}</button>
        ))}
      </div>

      <div style={S.scroll}>
        {/* Section paiements orphelins (sans chambre) — à nettoyer */}
        {orphanPayments.length > 0 && (
          <div style={S.orphanBox}>
            <div style={S.orphanHeader}>
              <span style={{ fontSize:16 }}>⚠️</span>
              <div style={{ flex:1 }}>
                <p style={S.orphanTitle}>Paiements sans chambre ({orphanPayments.length})</p>
                <p style={S.orphanSub}>Ces paiements ne sont liés à aucune chambre. Vous pouvez les supprimer.</p>
              </div>
            </div>
            {orphanPayments.map(p => (
              <div key={p.id} style={S.orphanRow}>
                <div style={{ flex:1 }}>
                  <p style={{ fontSize:12, fontWeight:600, color:"#8a4c00", margin:0 }}>
                    {(p.amount || LOYER).toLocaleString("fr-FR")} FCFA · {p.method || "—"}
                  </p>
                  <p style={{ fontSize:11, color:"#a06a30", margin:"2px 0 0" }}>
                    {p.status === "confirmed" ? "Confirmé ✓" : p.status === "pending" ? "À confirmer" : "Rejeté"} · {monthLabel(p.createdAt)}
                  </p>
                </div>
                <button
                  style={S.deleteBtn}
                  onClick={() => handleDeletePayment(p.id)}
                  disabled={deletingId === p.id}
                >
                  {deletingId === p.id ? "..." : "🗑️ Supprimer"}
                </button>
              </div>
            ))}
          </div>
        )}

        {filter === "confirmed" ? renderConfirmedGroups() : renderFlatList()}
      </div>

      {/* ── Modal confirmation paiement étudiant ── */}
      <Modal open={!!selected} onClose={() => { setSelected(null); setShowReject(false); setRejectReason(""); }} title="Confirmer le paiement">
        {selected && (
          <div>
            <div style={S.detailGrid}>
              <div style={S.dRow}><span style={S.dl}>Chambre</span><span style={S.dv}>{roomName(selected.roomId)}</span></div>
              <div style={S.dRow}><span style={S.dl}>Mois</span><span style={S.dv}>{selected.monthLabel || formatMonthValue(selected.month) || monthLabel(selected.createdAt)}</span></div>
              <div style={S.dRow}><span style={S.dl}>Montant</span><span style={{ ...S.dv, fontWeight:700, color:"#1a3a5c" }}>{LOYER.toLocaleString("fr-FR")} FCFA</span></div>
              <div style={S.dRow}><span style={S.dl}>Mode</span><span style={S.dv}>{METHOD_ICONS[selected.method]} {selected.method}</span></div>
              {selected.reference && <div style={S.dRow}><span style={S.dl}>Réf.</span><span style={{ ...S.dv, fontFamily:"monospace", fontSize:12 }}>{selected.reference}</span></div>}
              <div style={S.dRow}><span style={S.dl}>Déclaré le</span><span style={S.dv}>{monthLabel(selected.createdAt)}</span></div>
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:8, marginTop:14 }}>
              <Btn variant="primary" onClick={handleConfirm} disabled={confirming || rejecting}>
                {confirming ? "Confirmation..." : "✓ Confirmer"}
              </Btn>
              {!showReject ? (
                <Btn variant="danger" onClick={() => setShowReject(true)}>✕ Rejeter</Btn>
              ) : (
                <div style={{ background:"#fff5f5", border:"1px solid #f5b8b8", borderRadius:12, padding:"12px 14px" }}>
                  <p style={{ fontSize:13, color:"#a32d2d", fontWeight:600, margin:"0 0 8px" }}>Raison du rejet (optionnel)</p>
                  <textarea
                    style={{ width:"100%", border:"1.5px solid #f5b8b8", borderRadius:8, padding:"8px 10px", fontSize:13, fontFamily:"inherit", background:"white", color:"#1a2a3a", resize:"none", outline:"none", boxSizing:"border-box", marginBottom:10 }}
                    value={rejectReason} onChange={e => setRejectReason(e.target.value)}
                    placeholder="Ex: Référence incorrecte..." rows={2}
                  />
                  <div style={{ display:"flex", gap:8 }}>
                    <button style={{ flex:1, padding:"9px", borderRadius:8, background:"#a32d2d", color:"white", border:"none", fontSize:13, fontWeight:700, cursor:"pointer" }}
                      onClick={handleReject} disabled={rejecting}>{rejecting?"Rejet...":"✕ Confirmer le rejet"}</button>
                    <button style={{ flex:1, padding:"9px", borderRadius:8, background:"#f0f4f8", color:"#1a3a5c", border:"none", fontSize:13, fontWeight:600, cursor:"pointer" }}
                      onClick={() => { setShowReject(false); setRejectReason(""); }}>Annuler</button>
                  </div>
                </div>
              )}
              <Btn variant="ghost" onClick={() => { setSelected(null); setShowReject(false); setRejectReason(""); }}>Fermer</Btn>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Modal validation directe ── */}
      <Modal open={showDirectModal} onClose={() => setShowDirectModal(false)} title="Valider un paiement directement">
        <div>
          <div style={{ background:"#e3f1fb", borderRadius:10, padding:"10px 12px", marginBottom:14, fontSize:12, color:"#1a3a5c" }}>
            💡 Utilisez cette option pour enregistrer un paiement reçu en dehors de l'appli (espèces, virement direct, etc.)
          </div>

          <p style={S.fieldLabel}>Chambre</p>
          <select style={S.select} value={directForm.roomId} onChange={e => setDirectForm(f => ({ ...f, roomId: e.target.value }))}>
            <option value="">-- Choisir une chambre --</option>
            {rooms.filter(r => r.tenantName).map(r => (
              <option key={r.id} value={r.id}>Ch. {r.number} — {r.tenantName}</option>
            ))}
          </select>

          <p style={S.fieldLabel}>Mois concerné</p>
          <select style={S.select} value={directForm.month} onChange={e => setDirectForm(f => ({ ...f, month: e.target.value }))}>
            {monthOptions.map(o => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>

          <p style={S.fieldLabel}>Mode de paiement</p>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:8, marginBottom:14 }}>
            {METHODS.map(m => (
              <button key={m}
                style={{ padding:"10px 8px", borderRadius:10, border: directForm.method===m?"1.5px solid #2e86c1":"1.5px solid #d0dce8", background: directForm.method===m?"#e3f1fb":"white", color: directForm.method===m?"#1a3a5c":"#5a7a9a", cursor:"pointer", fontFamily:"inherit", fontSize:12, fontWeight:600, display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}
                onClick={() => setDirectForm(f => ({ ...f, method: m }))}
              >
                <span style={{ fontSize:18 }}>{METHOD_ICONS[m]}</span>{m}
              </button>
            ))}
          </div>

          {directForm.method !== "Espèces" && (
            <>
              <p style={S.fieldLabel}>Référence (optionnel)</p>
              <input style={S.input} value={directForm.reference}
                onChange={e => setDirectForm(f => ({ ...f, reference: e.target.value }))}
                placeholder="Référence de transaction" />
            </>
          )}

          <div style={{ display:"flex", flexDirection:"column", gap:8, marginTop:4 }}>
            <Btn variant="primary" onClick={handleDirectPayment} disabled={directSaving || !directForm.roomId}>
              {directSaving ? "Enregistrement..." : `✓ Valider — ${formatMonthValue(directForm.month)}`}
            </Btn>
            <Btn variant="ghost" onClick={() => setShowDirectModal(false)}>Annuler</Btn>
          </div>
        </div>
      </Modal>
    </div>
  );
}

const S = {
  page:    { display:"flex", flexDirection:"column", height:"100%", background:"#f5f8fc", fontFamily:"'Barlow',sans-serif" },
  topBar:  { padding:"14px 16px 8px", display:"flex", justifyContent:"space-between", alignItems:"center" },
  title:   { fontSize:22, fontWeight:800, color:"#1a3a5c", fontFamily:"'Barlow Condensed',sans-serif", margin:0 },
  directBtn:{ background:"#2d6a0a", color:"white", border:"none", borderRadius:10, padding:"9px 14px", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" },
  summaryRow:{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8, padding:"0 16px 12px" },
  sumCard: { background:"white", borderRadius:12, padding:"10px 8px", textAlign:"center", border:"0.5px solid #d0dce8" },
  sumNum:  { display:"block", fontSize:13, fontWeight:800, color:"#1a3a5c", fontFamily:"'Barlow Condensed',sans-serif" },
  sumLabel:{ display:"block", fontSize:10, color:"#7a90a8", marginTop:2 },
  reminderBtn:{ width:"100%", padding:"11px", borderRadius:12, background:"#1a3a5c", color:"white", border:"none", fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"inherit" },
  filterBar:{ display:"flex", gap:8, padding:"0 16px 12px", overflowX:"auto" },
  pill:    { padding:"6px 14px", borderRadius:20, border:"1.5px solid #d0dce8", background:"white", color:"#5a7a9a", fontSize:12, fontWeight:500, cursor:"pointer", whiteSpace:"nowrap", fontFamily:"inherit" },
  pillOn:  { background:"#1a3a5c", color:"white", borderColor:"#1a3a5c" },
  scroll:  { flex:1, overflowY:"auto", padding:"0 16px 80px" },

  // Groupes par mois
  monthGroup: { marginBottom:10 },
  monthHeader:{ width:"100%", display:"flex", justifyContent:"space-between", alignItems:"center", background:"white", border:"0.5px solid #d0dce8", borderRadius:14, padding:"14px 16px", cursor:"pointer", fontFamily:"inherit", textAlign:"left", boxSizing:"border-box" },
  monthTitle: { fontSize:15, fontWeight:800, color:"#1a3a5c", fontFamily:"'Barlow Condensed',sans-serif", textTransform:"capitalize" },
  monthSub:   { fontSize:12, color:"#5a7a9a", marginTop:2 },
  monthBadge: { background:"#e8f5e0", color:"#2d6a0a", borderRadius:20, padding:"3px 10px", fontSize:12, fontWeight:700 },
  monthBody:  { background:"white", borderRadius:"0 0 14px 14px", border:"0.5px solid #d0dce8", borderTop:"none", padding:"0 0 4px" },

  payCard: { background:"white", borderRadius:14, padding:"14px", marginBottom:10, border:"0.5px solid #d0dce8" },
  payHeader:{ display:"flex", gap:10, marginBottom:8 },
  payRoom: { fontSize:13, fontWeight:700, color:"#1a2a3a", margin:"0 0 2px" },
  payMeta: { fontSize:12, color:"#5a7a9a", margin:0 },
  payRef:  { fontSize:11, color:"#7a90a8", margin:"2px 0 0", fontFamily:"monospace" },
  payAmt:  { fontSize:14, fontWeight:700, color:"#1a3a5c", margin:"0 0 4px", fontFamily:"'Barlow Condensed',sans-serif" },
  confirmBtn:{ width:"100%", padding:"9px", borderRadius:8, background:"#e8f5e0", color:"#2d6a0a", border:"1.5px solid #a8d880", fontSize:13, fontWeight:600, cursor:"pointer", fontFamily:"inherit" },
  quittanceBtn:{ width:"100%", marginTop:8, padding:"8px", borderRadius:8, background:"#e3f1fb", color:"#1a3a5c", border:"1.5px solid #90bde0", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit" },
  detailGrid:{ background:"#f5f8fc", borderRadius:12, padding:"4px 0" },
  dRow:    { display:"flex", justifyContent:"space-between", alignItems:"center", padding:"7px 14px", borderBottom:"0.5px solid #e8f0f8" },
  dl:      { fontSize:13, color:"#7a90a8" },
  dv:      { fontSize:13, color:"#1a2a3a" },
  fieldLabel:{ fontSize:12, color:"#4a6a8a", fontWeight:500, marginBottom:8 },
  select:  { width:"100%", border:"1.5px solid #d0dce8", borderRadius:10, padding:"11px 12px", fontSize:13, fontFamily:"inherit", background:"#f7fafd", color:"#1a2a3a", outline:"none", boxSizing:"border-box", marginBottom:14, cursor:"pointer" },
  input:   { width:"100%", border:"1.5px solid #d0dce8", borderRadius:10, padding:"11px 12px", fontSize:13, fontFamily:"inherit", background:"#f7fafd", color:"#1a2a3a", outline:"none", boxSizing:"border-box", marginBottom:14 },

  // Paiements orphelins
  orphanBox:   { background:"#fffaf0", border:"1px solid #f5d8a0", borderRadius:14, padding:"12px 14px", marginBottom:14 },
  orphanHeader:{ display:"flex", gap:10, alignItems:"flex-start", marginBottom:10 },
  orphanTitle: { fontSize:13, fontWeight:700, color:"#8a4c00", margin:0 },
  orphanSub:   { fontSize:11, color:"#a06a30", margin:"2px 0 0" },
  orphanRow:   { display:"flex", alignItems:"center", gap:10, background:"white", borderRadius:10, padding:"10px 12px", marginTop:8, border:"0.5px solid #f0e0c0" },
  deleteBtn:   { padding:"7px 12px", borderRadius:8, background:"#fdecea", color:"#a32d2d", border:"1px solid #f5b8b8", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit", whiteSpace:"nowrap" },
};
